import React, { useEffect, useRef, useState } from "react";
import { ProgressSpinner } from "primereact/progressspinner";
import { Dropdown } from "primereact/dropdown";
import { Button } from "../../../../components/ui/Button";
import { formatDuration } from "../../../../config/global";
import { useSelector } from "react-redux";
import moment from "moment";
import { useTranslation } from "react-i18next";

import LazyImage from "../../../SharedComponents/ShowMedia/LazyImage";
import PlaylistContent from "./Content/PlaylistContent";
import LayoutsContent from "./Content/LayoutsContent";

const ContentList = ({ isOnline, isLoading, data, currentPlaygroundPlaying, currentBannerPlaying, currentCornerPlaying, currentMediaPlaying }) => {
  const { t } = useTranslation();
  const [tick, setTick] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const itemRefs = useRef([]);
  const scrollDivRef = useRef(null);
  const { playingOnCorner, playingOnPlayground, playingOnBanner, paused } = useSelector((state) => state.device);

  const [expandedItem, setExpandedItem] = useState(null);

  const toggleMute = () => {
    const mediaElements = document.querySelectorAll("audio, video");
    mediaElements.forEach((el) => {
      el.muted = !isMuted;
    });
    setIsMuted(!isMuted);
  };

  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  const [selectedLayer, setSelectedLayer] = useState({ name: t("devices.preview_layer_main_channel"), code: "MAIN" });

  const AllLayers = [
    { name: t("devices.preview_layer_main_channel"), code: "MAIN", icon: "fa-solid fa-clapperboard" },
    { name: t("devices.preview_layer_playground"), code: "PLAYGROUND", icon: "fa-solid fa-gamepad" },
    { name: t("devices.preview_layer_corner_spot"), code: "CORNER", icon: "fa-solid fa-thumbtack" },
    { name: t("devices.preview_layer_ticker_banner"), code: "BANNER", icon: "fa-solid fa-scroll" },
  ];

  const filteredLayers = AllLayers.filter((layer) => data?.hasOwnProperty(layer.code));

  const getProgressPercent = (startTime, duration) => {
    if (paused) return 0;
    const now = Date.now();
    const start = moment(startTime).valueOf();
    const end = start + duration * 1000;
    if (now >= end) return 100;
    const percent = ((now - start) / (end - start)) * 100;
    return Math.max(0, Math.min(100, percent));
  };

  const isItemPlaying = (item) => {
    const currentId = String(currentMediaPlaying?.currentMediaId ?? "");
    const itemId = String(item.assigned_id || item.layout_id || item.id || "");
    
    if (item?.playlist?.MAIN) {
      const mainList = item?.playlist?.MAIN ?? [];
      for (const entry of mainList) {
        if (String(entry?.assigned_id ?? "") === currentId) return true;
      }
    }
    
    switch (selectedLayer.code) {
      case "MAIN": return currentId === itemId;
      case "CORNER": return String(playingOnCorner) === itemId;
      case "PLAYGROUND": return String(playingOnPlayground) === itemId;
      case "BANNER": return String(playingOnBanner) === itemId;
      default: return false;
    }
  };

  useEffect(() => {
    if (!data || !selectedLayer?.code || !data[selectedLayer.code]) return;
    const allItems = Object.values(data[selectedLayer.code]).flat();
    const index = allItems.findIndex(item => isItemPlaying(item));

    if (index !== -1 && itemRefs.current[index]) {
      setTimeout(() => {
        itemRefs.current[index].scrollIntoView({ behavior: "smooth", block: "center" });
      }, 300);
    }
  }, [currentMediaPlaying, selectedLayer.code]);

  return (
    <div className="flex flex-col h-full bg-slate-50/50">
      {/* HEADER: Layer Controls */}
      <div className="p-4 bg-white border-b border-slate-100 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-indigo-500/10 flex items-center justify-center text-indigo-600">
               <i className={selectedLayer.icon || "fa-solid fa-layer-group"}></i>
            </div>
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">{t("devices.preview_signal_layer")}</span>
          </div>
          <div className="flex items-center gap-1">
             <button onClick={toggleMute} className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${isMuted ? 'bg-red-50 text-red-500' : 'bg-slate-50 text-slate-500'}`}>
                <i className={`fa-solid ${isMuted ? 'fa-volume-xmark' : 'fa-volume-high'}`}></i>
             </button>
          </div>
        </div>

        <Dropdown
          value={selectedLayer}
          onChange={(e) => setSelectedLayer(e.value)}
          options={filteredLayers}
          optionLabel="name"
          placeholder={t("devices.preview_select_spectrum")}
          className="w-full premium-dropdown rounded-xl border-slate-200"
          contentClassName="rounded-xl"
        />
      </div>

      {/* MEDIA LIST */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3 custom-scrollbar" ref={scrollDivRef}>
        {isLoading ? (
          <div className="flex h-full items-center justify-center py-10">
            <ProgressSpinner style={{ width: "32px", height: "32px" }} strokeWidth="4" />
          </div>
        ) : !data || !data[selectedLayer.code] ? (
          <div className="flex flex-col items-center justify-center py-20 text-slate-400 gap-4">
            <i className="fa-solid fa-ghost text-4xl opacity-20"></i>
            <span className="text-[10px] font-bold uppercase tracking-[0.2em]">{t("devices.preview_zero_feed_data")}</span>
          </div>
        ) : (
          Object.values(data[selectedLayer.code]).flat().map((item, index) => {
            const playing = isItemPlaying(item);
            const startTime = (() => {
              switch (item.layer) {
                case "PLAYGROUND": return currentPlaygroundPlaying?.start_time;
                case "CORNER": return currentCornerPlaying?.start_time;
                case "BANNER": return currentBannerPlaying?.start_time;
                case "MAIN": return currentMediaPlaying?.start_time;
                default: return null;
              }
            })();
            
            const progress = playing ? getProgressPercent(startTime, item.custom_duration) : 0;
            const hasSubContent = item.playlist_name || item.layout_name;

            return (
              <div 
                key={index} 
                ref={(el) => (itemRefs.current[index] = el)}
                className={`group relative rounded-2xl border transition-all duration-500 overflow-hidden ${
                  playing 
                    ? 'bg-white border-[var(--pp-btn-honey)] shadow-lg shadow-[color-mix(in_srgb,var(--pp-btn-honey)_20%,transparent)] scale-[1.02]' 
                    : 'bg-white/60 border-slate-100 hover:border-slate-300'
                }`}
              >
                {/* Playing Indicator Bar */}
                {playing && (
                  <div className="absolute top-0 left-0 w-1 h-full bg-[var(--pp-btn-honey)] z-10"></div>
                )}

                <div className="p-3">
                  <div className="flex items-center gap-3">
                    {/* Media Thumbnail/Icon */}
                    <div className="w-12 h-12 rounded-xl bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center shrink-0">
                      {item.media_type ? (
                        [ "HDMI", "CHANNEL", "RSS", "RSS_CUSTOM", "MULTI_3D"].includes(item.media_type) ? (
                          <div className="text-[8px] font-black leading-tight text-center px-1 text-slate-500">
                             {item.media_type.replace(/_/g, "\n")}
                          </div>
                        ) : ["WEATHER", "CLOCK"].includes(item.original_media_type) ? (
                          <i className={`fa-solid ${item.original_media_type === 'WEATHER' ? 'fa-cloud-sun' : 'fa-clock'} text-slate-400`}></i>
                        ) : (
                          <LazyImage media={item} popupType="playlist_media_setting" index={item.id} styleClass="w-full h-full object-cover" />
                        )
                      ) : (
                        <div className="w-full h-full bg-indigo-50 flex items-center justify-center">
                           <i className={`fa-solid ${item.layout_name ? 'fa-table-cells' : 'fa-list-ul'} text-indigo-400`}></i>
                        </div>
                      )}
                    </div>

                    {/* Media Info */}
                    <div className="flex-1 min-w-0">
                       <h5 className={`m-0 text-xs font-bold truncate ${playing ? 'text-[var(--pp-btn-honey)]' : 'text-slate-700'}`}>
                          {item.media_name || item.playlist_name || item.layout_name}
                       </h5>
                       <div className="flex items-center gap-2 mt-1">
                          <span className="text-[9px] font-bold text-slate-400 tracking-wider">
                             {formatDuration(item.custom_duration)}
                          </span>
                          {playing && (
                             <span className="flex items-center gap-1">
                                <span className="w-1 h-1 rounded-full bg-[var(--pp-btn-honey)] animate-ping"></span>
                                <span className="text-[9px] font-black text-[var(--pp-btn-honey)] uppercase tracking-tighter">{t("devices.preview_on_air")}</span>
                             </span>
                          )}
                       </div>
                    </div>

                    {/* Interaction Button */}
                    {hasSubContent && (
                      <button 
                        onClick={() => setExpandedItem(expandedItem === index ? null : index)}
                        className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all ${expandedItem === index ? 'bg-slate-800 text-white' : 'text-slate-400 hover:bg-slate-100'}`}
                      >
                        <i className={`fa-solid fa-chevron-${expandedItem === index ? 'up' : 'down'} text-[10px]`}></i>
                      </button>
                    )}
                  </div>

                  {/* Progress Line */}
                  {playing && (
                    <div className="mt-3 h-1 w-full bg-slate-100 rounded-full overflow-hidden">
                       <div 
                         className="h-full bg-[var(--pp-btn-honey)] transition-all duration-1000 ease-linear shadow-[0_0_8px_color-mix(in_srgb,var(--pp-btn-honey)_50%,transparent)]"
                         style={{ width: `${progress}%` }}
                       ></div>
                    </div>
                  )}
                </div>

                {/* Expanded Content (Playlists/Layouts) */}
                {expandedItem === index && hasSubContent && (
                  <div className="bg-slate-50/80 border-t border-slate-100 p-4 animate-in slide-in-from-top-2 duration-300">
                    {item.playlist_name ? (
                      <PlaylistContent
                        itIsPlaying={playing}
                        item={item}
                        currentMediaPlaying={currentMediaPlaying.currentMediaId}
                        startTime={currentMediaPlaying.start_time}
                        pause_start_time={currentMediaPlaying.pause_start_time}
                      />
                    ) : (
                      <LayoutsContent
                        itIsPlaying={playing}
                        item={item}
                        startTime={currentMediaPlaying.start_time}
                        pause_start_time={currentMediaPlaying.pause_start_time}
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* FOOTER: Scroll Navigation */}
      <div className="p-3 bg-white border-t border-slate-100 flex items-center justify-center gap-4">
         <button 
           onClick={() => scrollDivRef.current?.scrollTo({ top: 0, behavior: "smooth" })}
           className="w-10 h-10 rounded-full border border-slate-100 text-slate-400 hover:text-indigo-600 hover:border-indigo-100 transition-all shadow-sm flex items-center justify-center"
         >
            <i className="fa-solid fa-arrow-up text-sm"></i>
         </button>
         <button 
           onClick={() => {
               if (!data || !selectedLayer?.code || !data[selectedLayer.code]) return;
               const allItems = Object.values(data[selectedLayer.code]).flat();
               const idx = allItems.findIndex(item => isItemPlaying(item));
               if (idx !== -1 && itemRefs.current[idx]) {
                 itemRefs.current[idx].scrollIntoView({ behavior: "smooth", block: "center" });
               }
            }}
           className="px-5 py-2 rounded-full bg-[var(--pp-btn-honey)] text-white font-bold text-[10px] tracking-widest uppercase shadow-lg shadow-[color-mix(in_srgb,var(--pp-btn-honey)_20%,transparent)] flex items-center gap-2 hover:bg-[var(--pp-btn-honey-hover)] active:scale-95 transition-all"
         >
            <i className="fa-solid fa-crosshairs"></i>
            {t("devices.preview_find_active")}
         </button>
      </div>
    </div>
  );
};

export default ContentList;
